import React from "react";

import { Input } from "antd";

import ColorPicker from "../../../common/ColorPicker";
import { MaterialIconType } from "../../../common/Icons/MaterialIcons";
import Message from "../../../common/Message";
import { ModalNew } from "../../../common/Modal";
import { ensureHexValue } from "../../../common/utils";

import { Data, useUpdateQueueSettings } from "./queries";
import * as styled from "./styledComponents";

const MAX_DESCRIPTION_LENGTH = 1000;

interface QueueSettingsInput {
  id: string;
  name: string;
  description: string;
  icon?: string;
  color?: string;
}

export default function SettingsModal({
  queue,
  onCancel,
  onComplete
}: {
  queue: QueueSettingsInput;
  onCancel: () => void;
  onComplete: () => void;
}) {
  const [draftQueue, setDraftQueue] = React.useState(queue);
  const [updateQueue, { loading, error }] = useUpdateQueueSettings({
    onCompleted: (data: Data) => {
      if (data.updateQueueSettings.__typename === "ValidationErrorResult") {
        const message = data.updateQueueSettings.messages
          .map(message => message.text)
          .join(" ");
        Message.error(message);
      } else {
        Message.success("Queue settings updated.");
        onComplete();
      }
    }
  });

  React.useEffect(() => {
    if (error) {
      Message.error("Something went wrong. Please try again.");
    }
  }, [error]);

  const onChangeQueue = (change: Partial<QueueSettingsInput>) => {
    setDraftQueue({
      ...draftQueue,
      ...change
    });
  };

  return (
    <ModalNew
      visible
      title="Queue settings"
      confirmLoading={loading}
      onOk={async () => {
        const { id, name, icon, color, description } = draftQueue;
        updateQueue({
          variables: {
            queue: {
              id,
              name,
              icon,
              color,
              description
            }
          }
        });
      }}
      onCancel={onCancel}
    >
      <label>
        Name
        <styled.Input
          data-test="spaceNameInput"
          value={draftQueue.name}
          onChange={ev => {
            onChangeQueue({ name: ev.target.value });
          }}
        />
      </label>
      <styled.Cols>
        <styled.IconSelectLabel>
          Icon (optional)
          <styled.IconSelect
            data-test="iconSelectDropdown"
            value={draftQueue.icon}
            color={draftQueue.color}
            onChange={(icon: MaterialIconType) => {
              onChangeQueue({ icon });
            }}
          />
        </styled.IconSelectLabel>
        <label>
          Color
          <ColorPicker
            value={ensureHexValue(draftQueue.color || "")}
            strategy="fixed"
            border={false}
            onChange={color => {
              onChangeQueue({ color: color.substring(1) });
            }}
          ></ColorPicker>
        </label>
      </styled.Cols>
      <label>
        Description (optional)
        <Input.TextArea
          data-test="spaceDescInput"
          value={draftQueue.description}
          maxLength={1000}
          onChange={ev => {
            onChangeQueue({ description: ev.target.value });
          }}
        />
        <styled.LimitCounter
          current={draftQueue.description.length}
          limit={MAX_DESCRIPTION_LENGTH}
        />
      </label>
    </ModalNew>
  );
}
